import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { T, TA, BI, Img, Field, inp } from "./fields";

function FieldRenderer({ field, value, onChange, testId }) {
    switch (field.type) {
        case "text":
            return <T label={field.label} value={value} onChange={onChange} testId={testId} />;
        case "number":
            return <T label={field.label} value={value} onChange={onChange} testId={testId} type="number" />;
        case "textarea":
            return <TA label={field.label} value={value} onChange={onChange} testId={testId} />;
        case "bi":
            return <BI label={field.label} value={value} onChange={onChange} testId={testId} />;
        case "biarea":
            return <BI label={field.label} value={value} onChange={onChange} testId={testId} rows={3} />;
        case "image":
            return <Img label={field.label} value={value} onChange={onChange} testId={testId} />;
        case "switch":
            return (
                <div className="flex items-center gap-3 pt-1">
                    <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
                    <span className="text-sm">{field.label}</span>
                </div>
            );
        default:
            return null;
    }
}

export default function CrudTab({ resource, title, fields, empty }) {
    const [items, setItems] = useState(null);
    const [editing, setEditing] = useState(null);
    const [isNew, setIsNew] = useState(false);

    const load = useCallback(() => {
        api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => toast.error(`Failed to load ${resource}`));
    }, [resource]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            if (isNew) await api.post(`/admin/${resource}`, editing);
            else await api.put(`/admin/${resource}/${editing.id}`, editing);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        await api.delete(`/admin/${resource}/${id}`);
        toast.success("Deleted");
        load();
    };

    const toggle = async (item, field) => {
        const updated = { ...item, [field.key]: !item[field.key] };
        await api.put(`/admin/${resource}/${item.id}`, updated);
        load();
    };

    const move = async (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const a = { ...items[i], order: j + 1 };
        const b = { ...items[j], order: i + 1 };
        await api.put(`/admin/${resource}/${a.id}`, a);
        await api.put(`/admin/${resource}/${b.id}`, b);
        load();
    };

    if (!items) return <p className="text-sm text-neutral-500">Loading…</p>;

    const switchFields = fields.filter((f) => f.type === "switch" && f.row);
    const biText = (v) => (v && typeof v === "object" ? v.en || v.pt || "" : v || "");

    return (
        <div data-testid={`${resource}-tab`}>
            <div className="mb-6 flex items-center justify-between">
                <h2 className="font-display text-3xl">{title.toUpperCase()}</h2>
                <button
                    onClick={() => { setEditing({ ...empty }); setIsNew(true); }}
                    data-testid={`${resource}-add-btn`}
                    className="bg-[#D60024] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#0A0A0A] transition-colors"
                >
                    Add +
                </button>
            </div>

            <div className="divide-y divide-neutral-200 border border-neutral-200 bg-white">
                {items.map((item, i) => (
                    <div key={item.id} className="flex items-center gap-4 px-4 py-3" data-testid={`${resource}-row-${i}`}>
                        <div className="flex flex-col">
                            <button onClick={() => move(i, -1)} data-testid={`${resource}-up-${i}`} className="text-neutral-400 hover:text-[#D60024] text-xs leading-none" aria-label="Move up">▲</button>
                            <button onClick={() => move(i, 1)} data-testid={`${resource}-down-${i}`} className="text-neutral-400 hover:text-[#D60024] text-xs leading-none" aria-label="Move down">▼</button>
                        </div>
                        {(item.photo_url || item.image_url || item.logo_url) && (
                            <img src={item.photo_url || item.image_url || item.logo_url} alt="" className="h-12 w-12 border border-neutral-200 object-cover grayscale" />
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">{biText(item.name)}</p>
                            <p className="truncate text-xs text-neutral-500">{biText(item.role) || biText(item.category) || item.client || ""}</p>
                        </div>
                        {switchFields.map((f) => (
                            <Switch key={f.key} checked={!!item[f.key]} onCheckedChange={() => toggle(item, f)} data-testid={`${resource}-${f.key}-switch-${i}`} />
                        ))}
                        <button onClick={() => { setEditing({ ...item }); setIsNew(false); }} data-testid={`${resource}-edit-${i}`} className="text-xs font-semibold uppercase tracking-wider text-neutral-600 hover:text-[#D60024]">Edit</button>
                        <button onClick={() => remove(item.id)} data-testid={`${resource}-delete-${i}`} className="text-xs font-semibold uppercase tracking-wider text-neutral-400 hover:text-[#D60024]">Delete</button>
                    </div>
                ))}
                {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-neutral-400">No items yet.</p>}
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">{isNew ? `NEW ${title.slice(0, -1).toUpperCase()}` : "EDIT"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-4 pt-2">
                            {fields.map((f) => (
                                <FieldRenderer
                                    key={f.key}
                                    field={f}
                                    value={editing[f.key]}
                                    onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))}
                                    testId={`${resource}-field-${f.key}`}
                                />
                            ))}
                            <button onClick={save} data-testid={`${resource}-save-btn`} className="w-full bg-[#0A0A0A] py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white hover:bg-[#D60024] transition-colors">
                                Save
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
